import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Phone, User, UserCheck, UserPlus, X, Loader2, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Normalize mobile: strip +91, 0, spaces, dashes → 10 digits
const normalizeMobile = (raw) => {
  const digits = raw.replace(/\D/g, '');
  if (digits.startsWith('91') && digits.length === 12) return digits.slice(2);
  if (digits.startsWith('0') && digits.length === 11) return digits.slice(1);
  return digits.length === 10 ? digits : '';
};

/*
  Status flow:
  idle → searching → found | not_found → linked
  At any point → idle (clear)
*/
export default forwardRef(function CustomerTagSection({ user, onCustomerChange, getAuthHeaders, disabled }, ref) {
  const [mobile, setMobile] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | searching | found | not_found | linked | error
  const [customer, setCustomer] = useState(null);
  const [tagStatus, setTagStatus] = useState('guest'); // guest | existing | new
  const [errorMsg, setErrorMsg] = useState('');
  const [creating, setCreating] = useState(false);
  const debounceRef = useRef(null);

  const clientId = user?.client_id || user?.franchise?.client_id || '';
  const franchiseId = user?.franchise?.id || user?.franchise?.franchise_id || '';
  const franchiseName = user?.franchise?.name || user?.franchise?.franchise_name || '';
  const brandName = user?.business_name || user?.franchise?.brand_name || '';

  // Propagate customer state to parent
  const propagate = useCallback((cust, tag) => {
    if (onCustomerChange) {
      onCustomerChange({
        customer_id: cust?.customer_id || null,
        customer_name_snapshot: cust?.name || name || null,
        customer_mobile_snapshot: cust?.normalized_mobile || normalizeMobile(mobile) || null,
        customer_tag_status: tag,
        customer_name: cust?.name || name || null,
        customer_mobile: cust?.normalized_mobile || normalizeMobile(mobile) || null,
      });
    }
  }, [onCustomerChange, name, mobile]);

  // Auto-lookup when 10 valid digits entered
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const normalized = normalizeMobile(mobile);
    if (normalized.length === 10 && status !== 'linked') {
      debounceRef.current = setTimeout(() => lookupCustomer(normalized), 400);
    } else if (normalized.length < 10) {
      if (status !== 'idle' && status !== 'linked') {
        setStatus('idle');
        setCustomer(null);
        setErrorMsg('');
      }
    }
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [mobile]);

  const lookupCustomer = async (normalized) => {
    setStatus('searching');
    setErrorMsg('');
    try {
      const resp = await fetch(`${API}/customers/lookup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders?.() },
        body: JSON.stringify({ mobile: normalized, client_id: clientId, franchise_id: franchiseId }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Lookup failed');

      if (data.exists) {
        setCustomer(data.customer);
        setName(data.customer.name || '');
        setStatus('found');
        // Auto-attach existing
        setTagStatus('existing');
        setStatus('linked');
        propagate(data.customer, 'existing');
      } else {
        setCustomer(null);
        setStatus('not_found');
      }
    } catch (err) {
      console.error('Customer lookup error:', err);
      setStatus('error');
      setErrorMsg(err.message || 'Lookup failed');
      // Don't block billing on error — allow guest flow
    }
  };

  const handleCreate = async () => {
    const normalized = normalizeMobile(mobile);
    if (!normalized) return;

    setCreating(true);
    try {
      const resp = await fetch(`${API}/customers/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders?.() },
        body: JSON.stringify({
          mobile: normalized,
          name: name.trim(),
          client_id: clientId,
          franchise_id: franchiseId,
          franchise_name: franchiseName,
          brand_name: brandName,
          user_id: franchiseId,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || 'Creation failed');

      setCustomer(data.customer);
      setTagStatus(data.is_new ? 'new' : 'existing');
      setStatus('linked');
      propagate(data.customer, data.is_new ? 'new' : 'existing');
    } catch (err) {
      console.error('Customer create error:', err);
      setErrorMsg(err.message || 'Failed to create customer');
      setStatus('error');
    } finally {
      setCreating(false);
    }
  };

  const handleClear = () => {
    setMobile('');
    setName('');
    setStatus('idle');
    setCustomer(null);
    setTagStatus('guest');
    setErrorMsg('');
    propagate(null, 'guest');
  };

  // Reset method exposed via ref or called by parent
  const reset = () => handleClear();

  // Expose reset via ref
  useImperativeHandle(ref, () => ({ reset }), []);

  const linkedBadgeColor = tagStatus === 'existing'
    ? 'bg-blue-50 border-blue-200 text-blue-700'
    : 'bg-green-50 border-green-200 text-green-700';

  const linkedIcon = tagStatus === 'existing' ? UserCheck : UserPlus;
  const LinkedIcon = linkedIcon;

  return (
    <div data-testid="customer-tag-section" className="w-full">
      {/* Linked state — compact badge */}
      {status === 'linked' && customer ? (
        <div
          data-testid="customer-linked-badge"
          className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${linkedBadgeColor} transition-all`}
        >
          <LinkedIcon className="h-4 w-4 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-semibold truncate">
                {customer.name || customer.normalized_mobile}
              </span>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                tagStatus === 'existing' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
              }`}>
                {tagStatus === 'existing' ? 'Returning' : 'New'}
              </span>
              {customer.is_new_to_brand && tagStatus === 'existing' && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-amber-100 text-amber-600">
                  New to brand
                </span>
              )}
            </div>
            {customer.name && (
              <span className="text-[10px] opacity-70">{customer.normalized_mobile}</span>
            )}
          </div>
          <button
            data-testid="customer-clear-btn"
            onClick={handleClear}
            className="p-1 rounded-full hover:bg-black/5 transition-colors flex-shrink-0"
            title="Remove customer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : disabled ? (
        /* Frozen state — party ledger selected */
        <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50 border border-dashed border-border" data-testid="customer-disabled-state">
          <Phone className="h-3.5 w-3.5 text-muted-foreground/50" />
          <span className="text-xs text-muted-foreground/60">Customer tagging disabled — Party Ledger active</span>
        </div>
      ) : (
        /* Input state */
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            {/* Mobile input */}
            <div className="relative flex-1">
              <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                data-testid="customer-mobile-input"
                placeholder="Customer mobile"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                className="h-9 pl-8 pr-2 text-sm"
                type="tel"
                maxLength={13}
                inputMode="numeric"
              />
              {status === 'searching' && (
                <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </div>

            {/* Name input — show when not_found or when searching/found */}
            {(status === 'not_found' || (status === 'found' && !customer?.name)) && (
              <div className="relative flex-1">
                <User className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  data-testid="customer-name-input"
                  placeholder="Name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-9 pl-8 text-sm"
                />
              </div>
            )}

            {/* Action button */}
            {status === 'not_found' && (
              <Button
                data-testid="customer-create-btn"
                size="sm"
                onClick={handleCreate}
                disabled={creating}
                className="h-9 px-3 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold whitespace-nowrap"
              >
                {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (
                  <>
                    <UserPlus className="h-3.5 w-3.5 mr-1" />
                    Add
                  </>
                )}
              </Button>
            )}

            {/* Clear button */}
            {mobile && status !== 'idle' && (
              <button
                data-testid="customer-clear-input-btn"
                onClick={handleClear}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                title="Clear"
              >
                <X className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          {/* Status messages */}
          {status === 'not_found' && (
            <p data-testid="customer-status-new" className="text-[10px] text-amber-600 flex items-center gap-1 pl-1">
              <AlertCircle className="h-3 w-3" />
              New customer — tap Add to create
            </p>
          )}
          {status === 'error' && (
            <p data-testid="customer-status-error" className="text-[10px] text-red-500 flex items-center gap-1 pl-1">
              <AlertCircle className="h-3 w-3" />
              {errorMsg || 'Lookup failed'} — billing as guest
            </p>
          )}
        </div>
      )}
    </div>
  );
});
